import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import CommitmentService from "../../../api/services/commitment-service";
import { FIELD_TYPES } from "../constants";
import { isNonEmptyArray, isObject, isString, toCamelCase } from "../../../common/utils";
import { findDefaultTheme } from "../utils";

const initialState = {
	commitments: [],
	totalCommitments: 0,
	themes: [],
	themeFieldUuid: null,
	filters: {},
	appliedFilters: {},
	filterDefinitions: [],
	isThereAnotherFilter: false,
	filterOptionsCache: {},
	selectedTheme: null,
	selectedThemeUuid: null,
	selectedCommitment: null,
	selectedCommitmentFieldDetails: {},
	page: 0,
	limit: 25,
	orderBy: null,
	dir: "asc",
	isLoading: false,
	isLoadingCommitments: false,
	openDrawer: true,
	leftPanelWidth: 20,
	rightPanelWidth: 30,
	rightPanelView: "details",
	sidePanelOpen: false,
	showCommentsPanel: false,
	showEditPanel: false,
	selectedRows: [],
	selectionMode: "none",
	openFilterKey: null,
	progress: null,
	hasLaunchFinished: false,
	manualSelectionThemeUuuid: null,
};

const commitmentTrackingSlice = createSlice({
	name: "commitmentTracking",
	initialState,
	reducers: {
		setHasLaunchFinished: (state, { payload }) => ({ ...state, hasLaunchFinished: payload }),
		setOngoingProgress: (state, { payload }) => ({ ...state, progress: payload }),
		setCommitmentFilters: (state, { payload }) => {
			const themeField = payload.find((field) => field.fieldName.toLowerCase() === "theme");
			return {
				...state,
				themeFieldUuid: themeField?.fieldUuid || state.themeFieldUuid,
				filterDefinitions: payload,
			};
		},
		setPossibleValues: (state, { payload }) => ({
			...state,
			filterDefinitions: state.filterDefinitions.map(
				(filterDefinition) =>
					(payload.fieldUuid === filterDefinition.fieldUuid && {
						...filterDefinition,
						possibleValues: payload.possibleValues,
					}) ||
					filterDefinition
			),
		}),
		setCommitments: (state, action) => {
			state.commitments = action.payload;
		},
		setCommitment: (state, action) => {
			const { commitmentId, field, value } = action.payload;
			return {
				...state,
				commitments: state.commitments.map(
					(commitment) =>
						(commitment.commitmentId === commitmentId && { ...commitment, [field]: value }) || commitment
				),
			};
		},
		setTotalCommitments: (state, action) => {
			state.totalCommitments = action.payload;
		},
		setFilters: (state, action) => {
			state.filters = action.payload || { separator: "AND" };
			state.isThereAnotherFilter = Object.keys(action.payload || {}).some(
				(key) => key !== "separator" && key !== "content"
			);
		},
		setAppliedFilters: (state, action) => {
			state.appliedFilters = action.payload || { separator: "AND" };
		},
		setSelectedTheme: (state, action) => {
			state.selectedTheme = action.payload;
		},
		setManualSelectionThemeUuuid: (state, action) => {
			state.manualSelectionThemeUuuid = action.payload;
		},
		setSelectedThemeUuid: (state, action) => {
			state.selectedThemeUuid = action.payload;
		},
		setSelectedCommitment: (state, action) => {
			state.selectedCommitment = action.payload;
			if (action.payload === null) {
				state.selectedCommitmentFieldDetails = {};
			}
		},
		setPage: (state, action) => {
			state.page = action.payload;
		},
		setLimit: (state, action) => {
			state.limit = action.payload;
		},
		setOrderBy: (state, action) => {
			state.orderBy = action.payload;
		},
		setDir: (state, action) => {
			state.dir = action.payload;
		},
		setOpenDrawer: (state, action) => {
			state.openDrawer = action.payload;
		},
		setLeftPanelWidth: (state, action) => {
			state.leftPanelWidth = action.payload;
		},
		setRightPanelWidth: (state, action) => {
			state.rightPanelWidth = action.payload;
		},
		setSidePanelOpen: (state, action) => {
			state.sidePanelOpen = action.payload;
		},
		setShowCommentsPanel: (state, action) => {
			state.showCommentsPanel = action.payload;
		},
		setShowEditPanel: (state, action) => {
			state.showEditPanel = action.payload;
		},
		setOpenFilterKey: (state, action) => {
			state.openFilterKey = action.payload;
		},
		setFilterOptionsCache: (state, action) => {
			const { fieldUuid, options } = action.payload;
			state.filterOptionsCache[fieldUuid] = options;
		},
		setThemeFieldUuid: (state, action) => {
			state.themeFieldUuid = action.payload;
		},
		setFilterDefinitions: (state, action) => {
			state.filterDefinitions = action.payload;
		},
		setThemes: (state, action) => {
			state.themes = action.payload;
		},
		setSelectedCommitmentFieldDetails: (state, action) => {
			const { fieldUuid, details } = action.payload;
			state.selectedCommitmentFieldDetails[fieldUuid] = details;
		},
		incrementThemeCount: (state, action) => {
			const { themeName, themeUuid } = action.payload;
			const existingTheme = state.themes.find((t) => t.themeName === themeName || t.themeUuid === themeUuid);

			if (existingTheme) {
				existingTheme.commitmentCount = (existingTheme.commitmentCount || 0) + 1;
			} else {
				state.themes.push({
					themeName: themeName || themeUuid,
					themeUuid: themeUuid || themeName,
					commitmentCount: 1,
				});
			}
		},
		addCommitmentToList: (state, action) => {
			const newCommitment = action.payload;
			state.commitments.unshift(newCommitment);
			state.totalCommitments += 1;
		},
		decreaseThemeCount: (state, action) => {
			const { themeName, count } = action.payload;
			const theme = state.themes.find((t) => t.themeName === themeName);
			if (theme) {
				theme.commitmentCount = Math.max(0, (theme.commitmentCount || 0) - count);
			}
		},
		setRightPanelView: (state, action) => {
			state.rightPanelView = action.payload;
		},
		setSelectedRows: (state, action) => {
			state.selectedRows = action.payload;
		},
		setSelectionMode: (state, action) => {
			state.selectionMode = action.payload;
		},
		resetState: (state) => ({ ...initialState, progress: state.progress }),
		setCommentIndicator: (state, { payload }) => ({
			...state,
			commitments: state.commitments.map(
				(commitment) =>
					(payload.commitmentId === commitment.commitmentId && {
						...commitment,
						hasComments: payload.indicator,
					}) ||
					commitment
			),
		}),
	},
	extraReducers: (builder) => {
		builder
			.addCase("commitmentTracking/fetchCommitments/pending", (state, action) => {
				if (action.meta.arg.page === 0) {
					state.isLoadingCommitments = true;
				}
				state.isLoading = true;
			})
			.addCase("commitmentTracking/fetchCommitments/fulfilled", (state, action) => {
				state.isLoading = false;
				state.isLoadingCommitments = false;
				if (action.meta.arg.page === 0) {
					state.commitments = action.payload.contents || [];
				} else {
					state.commitments = [...state.commitments, ...(action.payload.contents || [])];
				}
				state.totalCommitments = action.payload.totalElements || 0;
			})
			.addCase("commitmentTracking/fetchCommitments/rejected", (state) => {
				state.isLoading = false;
				state.isLoadingCommitments = false;
			})
			.addCase("commitmentTracking/fetchFilters/fulfilled", (state, action) => {
				state.filterDefinitions = action.payload || [];
				const themeField = action.payload.find(
					(field) => field.fieldName.toLowerCase() === "thème" || field.fieldName.toLowerCase() === "theme"
				);
				if (themeField) {
					state.themeFieldUuid = themeField.fieldUuid;
				}
			})
			.addCase("commitmentTracking/updateThemeList/fulfilled", (state, action) => {
				const { fieldUuid, possibleValues } = action.payload;
				const newThemeId =
					state.selectedThemeUuid === null && !!state.selectedTheme
						? state.selectedThemeUuid
						: findDefaultTheme(possibleValues || [], state.selectedThemeUuid);
				return {
					...state,
					selectedTheme:
						state.selectedThemeUuid === null && !!state.selectedTheme
							? state.selectedTheme
							: possibleValues.find((theme) => theme.valueUuid === newThemeId)?.value || "",
					selectedThemeUuid: newThemeId || null,
					filterDefinitions: state.filterDefinitions.map(
						(filterDefinition) =>
							(fieldUuid === filterDefinition.fieldUuid && {
								...filterDefinition,
								possibleValues,
							}) ||
							filterDefinition
					),
				};
			})
			.addCase("commitmentTracking/deleteCommitments/fulfilled", (state, action) => {
				const deletedIds = action.payload;
				state.commitments = state.commitments.filter((c) => !deletedIds.includes(c.commitmentId));
				state.totalCommitments -= deletedIds.length;
				if (state.selectedCommitment && deletedIds.includes(state.selectedCommitment.commitmentId)) {
					state.selectedCommitment = null;
					state.selectedCommitmentFieldDetails = {};
					state.sidePanelOpen = false;
				}
			})
			.addCase("commitmentTracking/validateCommitment/fulfilled", (state, action) => {
				const { commitmentId, isValidated } = action.payload;
				const commitment = state.commitments.find((c) => c.commitmentId === commitmentId);
				if (commitment) {
					commitment.isValidated = isValidated;
				}
				if (state.selectedCommitment?.commitmentId === commitmentId) {
					state.selectedCommitment.isValidated = isValidated;
				}
			})
			.addCase("commitmentTracking/fetchCommitmentFieldDetails/fulfilled", (state, action) => {
				const { fieldUuid, details } = action.payload;
				state.selectedCommitmentFieldDetails[fieldUuid] = details;
			})
			.addCase("commitmentTracking/updateCommitment/fulfilled", (state, action) => {
				const index = state.commitments.findIndex((c) => c.commitmentId === action.payload.commitmentId);
				if (index !== -1) {
					state.commitments[index] = action.payload;
				}
				if (state.selectedCommitment?.commitmentId === action.payload.commitmentId) {
					state.selectedCommitment = action.payload;
				}
			});
	},
});

export const fetchCommitments = createAsyncThunk(
	"commitmentTracking/fetchCommitments",
	async ({ projectId, payload, page, limit, cancelToken }, { rejectWithValue }) => {
		try {
			const response = await CommitmentService.searchInCommitment(
				{ projectId },
				payload,
				{ page, limit },
				cancelToken
			);
			return response;
		} catch (error) {
			console.error("Error fetching commitments:", error);
			return rejectWithValue(error);
		}
	}
);

export const fetchFilters = createAsyncThunk(
	"commitmentTracking/fetchFilters",
	async ({ projectId, cancelToken }, { rejectWithValue }) => {
		try {
			const response = await CommitmentService.getCommitmentFilters({ projectId }, cancelToken);
			return response;
		} catch (error) {
			console.error("Error fetching filters:", error);
			return rejectWithValue(error);
		}
	}
);

export const createCommitment = createAsyncThunk(
	"commitmentTracking/createCommitment",
	async ({ projectId, payload, cancelToken }, { rejectWithValue }) => {
		try {
			const response = await CommitmentService.createCommitment({ projectId }, payload, cancelToken);
			return response;
		} catch (error) {
			console.error("Error creating commitment:", error);
			return rejectWithValue(error);
		}
	}
);

export const updateCommitment = createAsyncThunk(
	"commitmentTracking/updateCommitment",
	async ({ commitmentId, payload, cancelToken }, { rejectWithValue }) => {
		try {
			const response = await CommitmentService.updateCommitment({ commitmentId }, payload, cancelToken);
			return response;
		} catch (error) {
			console.error("Error updating commitment:", error);
			return rejectWithValue(error);
		}
	}
);

export const deleteCommitments = createAsyncThunk(
	"commitmentTracking/deleteCommitments",
	async ({ projectId, payload, cancelToken }, { rejectWithValue }) => {
		try {
			await CommitmentService.bulkDeleteCommitments({ projectId }, payload, cancelToken);
			return payload.includeIds || [];
		} catch (error) {
			console.error("Error bulk deleting commitments:", error);
			return rejectWithValue(error);
		}
	}
);

export const validateCommitment = createAsyncThunk(
	"commitmentTracking/validateCommitment",
	async ({ commitmentId, isValidated, cancelToken }, { rejectWithValue }) => {
		try {
			await CommitmentService.validateCommitment({ commitmentId, isValidated }, cancelToken);
			return { commitmentId, isValidated };
		} catch (error) {
			console.error("Error validating commitment:", error);
			return rejectWithValue(error);
		}
	}
);

export const fetchCommitmentFieldDetails = createAsyncThunk(
	"commitmentTracking/fetchCommitmentFieldDetails",
	async ({ commitmentId, fieldUuid, cancelToken }, { rejectWithValue }) => {
		try {
			const response = await CommitmentService.getCommitmentDetails({ commitmentId, fieldUuid }, cancelToken);
			return { commitmentId, fieldUuid, details: response };
		} catch (error) {
			console.error("Error fetching commitment field details:", error);
			return rejectWithValue(error);
		}
	}
);

const buildFilterPayload = (filters, filterDefinitions) => {
	const payload = {};
	Object.entries(filters).forEach(([fieldUuid, values]) => {
		if (fieldUuid === "content" || fieldUuid === "separator") {
			return;
		}
		if (!values || (Array.isArray(values) && values.length === 0)) {
			return;
		}
		const filterDefinition = filterDefinitions.find(
			(filterDef) =>
				filterDef.fieldUuid === fieldUuid ||
				(fieldUuid === "documents" && filterDef.type === FIELD_TYPES.DOCUMENT)
		);
		if (filterDefinition) {
			const { type, fieldName } = filterDefinition;
			const normalizedFieldName = toCamelCase(fieldName);
			if (type === FIELD_TYPES.DATE && isNonEmptyArray(values)) {
				payload[normalizedFieldName] = {
					operator: (values.length > 1 && "EQ") || values[0].relation || "EQ",
					startDate: values[0].value || null,
					endDate: values?.[1]?.value || null,
				};
			}
			if (type === FIELD_TYPES.CATEGORY) {
				if (isObject(values) && values?.is !== undefined && isNonEmptyArray(values.elements)) {
					payload[normalizedFieldName] = {
						is: values.is,
						matchAll: false,
						elements: values.elements.map(
							(element) =>
								(typeof element === "string" && element) || (isObject(element) && element?.valueUuid)
						),
					};
				}
				if (isNonEmptyArray(values)) {
					payload[normalizedFieldName] = {
						elements: values.map((val) => val.valueUuid),
						matchAll: false,
						is: true,
					};
				}
			}
			if (type === FIELD_TYPES.USER && isNonEmptyArray(values)) {
				payload[normalizedFieldName] = values.map((value) => value.value);
			}
			if (type === FIELD_TYPES.TEXT && isString(values)) {
				payload[normalizedFieldName] = values;
			}
			if (type === FIELD_TYPES.BOOLEAN) {
				payload[normalizedFieldName] = values.value;
			}
			if (type === FIELD_TYPES.DOCUMENT) {
				payload.documents = values;
			}
		}
	});
	if (filters?.content) {
		payload.details = filters.content;
	}
	return payload;
};

export const fetchFieldValues = createAsyncThunk(
	"commitmentTracking/fetchFieldValues",
	async ({ projectId, fieldUuid, filters = {}, cancelToken }, { getState, rejectWithValue }) => {
		try {
			const { commitmentTracking } = getState();
			const { filterDefinitions } = commitmentTracking;
			const payload = buildFilterPayload(filters, filterDefinitions);
			const response = await CommitmentService.getPossibleValuesOfField(
				{ projectId, fieldUuid },
				payload,
				cancelToken
			);
			const possibleValues =
				response.possibleValues?.map((item) => {
					if (typeof item === "string") {
						return { label: item, value: item };
					}
					return { ...item, label: item.value, commitmentCount: item.commitmentCount || 0 };
				}) || [];
			return { fieldUuid, possibleValues };
		} catch (error) {
			console.error("Error fetching field values:", error);
			return rejectWithValue(error);
		}
	}
);

export const updateThemeList = createAsyncThunk(
	"commitmentTracking/updateThemeList",
	async ({ projectId, filters, themeFieldUuid, cancelToken }, { dispatch, rejectWithValue }) => {
		try {
			const result = await dispatch(
				fetchFieldValues({
					projectId,
					fieldUuid: themeFieldUuid,
					filters,
					cancelToken,
				})
			).unwrap();
			return result;
		} catch (error) {
			console.error("Error updating theme list:", error);
			return rejectWithValue(error);
		}
	}
);

export const {
	setHasLaunchFinished,
	setOngoingProgress,
	setCommitments,
	setTotalCommitments,
	setFilters,
	setAppliedFilters,
	setSelectedTheme,
	setSelectedThemeUuid,
	setSelectedCommitment,
	setPage,
	setLimit,
	setOrderBy,
	setDir,
	setOpenDrawer,
	setLeftPanelWidth,
	setRightPanelWidth,
	setSidePanelOpen,
	setShowCommentsPanel,
	setShowEditPanel,
	setRightPanelView,
	setOpenFilterKey,
	setFilterOptionsCache,
	setThemeFieldUuid,
	setFilterDefinitions,
	setThemes,
	setSelectedCommitmentFieldDetails,
	incrementThemeCount,
	addCommitmentToList,
	decreaseThemeCount,
	setSelectedRows,
	setSelectionMode,
	resetState,
	setCommitmentFilters,
	setPossibleValues,
	setCommitment,
	setCommentIndicator,
	setManualSelectionThemeUuuid,
} = commitmentTrackingSlice.actions;

export default commitmentTrackingSlice.reducer;
